import React, { useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { IconSpinner, ListingCard, NamedLink } from '../../../../components';

import css from './SectionEmotoHero.module.css';

// The Marketplace API search schema is fixed to categoryLevel1..3 (see
// getBuiltInCategorySpecs in src/util/configHelpers.js). Category params are
// public data, so the SearchPage query param is prefixed with `pub_`.
// This matches PageBuilder's own SearchCTA/FilterCategories, which uses the
// Final Form field name "pub_categoryLevel1".
const CATEGORY_LEVEL1_PARAM = 'pub_categoryLevel1';

// How many top-level categories are surfaced as chips. The rest stay reachable
// through the category grid section and the search page filters.
const MAX_CHIPS = 6;

// How many listings the hero shelf shows. The featured-listings duck fetches more
// than this (MAX_LISTING_COUNT), so the extras are simply not rendered here.
const MAX_LISTINGS = 4;

/**
 * The newest listings, rendered as a shelf inside the hero.
 *
 * The data comes from the shared featured-listings duck: the hero section declares
 * `listingSelection: 'newest'` in src/containers/LandingPage/LandingPage.js, which is what
 * makes it eligible for the fetch. The request is made on mount (client side only), the same
 * way SectionListings does it.
 *
 * Renders nothing at all when there is no data to show — an empty shelf on a brand new
 * marketplace looks broken, and the category chips above already give the visitor a way in.
 * Fetch errors are logged by the duck, so they are swallowed here rather than replacing the
 * hero copy with an error block.
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId id of the hero section, used as the featured-listings key
 * @param {Array<Object>} props.allSections all sections on the page, needed by the duck
 * @param {Object} [props.featuredListings] featured listings props from PageBuilder
 * @returns {JSX.Element|null} the listing shelf, or null when there is nothing to show
 */
const HeroListings = props => {
  const { sectionId, allSections, featuredListings } = props;
  const config = useConfiguration();

  const { onFetchFeaturedListings, getListingEntitiesById, parentPage, featuredListingData } =
    featuredListings || {};

  const { listingIds, fetched, inProgress, error } = featuredListingData?.[sectionId] || {};

  const listingImageConfig = config.layout.listingImage;

  useEffect(() => {
    // Guard: the hero is also rendered outside the landing page (e.g. in the styleguide),
    // where there is no featured-listings wiring at all.
    if (onFetchFeaturedListings && !fetched && inProgress !== true && !error) {
      onFetchFeaturedListings(sectionId, parentPage, listingImageConfig, allSections);
    }
  }, []);

  if (!onFetchFeaturedListings || error) {
    return null;
  }

  if (inProgress) {
    return (
      <div className={css.listingsLoading}>
        <IconSpinner />
      </div>
    );
  }

  const listings = (listingIds ? getListingEntitiesById(listingIds) : []).slice(0, MAX_LISTINGS);

  if (listings.length === 0) {
    return null;
  }

  return (
    <div className={css.listings}>
      <div className={css.listingsHeader}>
        <h2 className={css.listingsHeading}>
          <FormattedMessage id="SectionEmotoHero.listingsHeading" />
        </h2>
        <NamedLink className={css.listingsLink} name="SearchPage">
          <FormattedMessage id="SectionEmotoHero.listingsLink" />
        </NamedLink>
      </div>

      <ul className={css.listingsGrid} role="list">
        {listings.map(listing => (
          <li key={listing.id.uuid} className={css.listingsItem}>
            <ListingCard
              listing={listing}
              renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 260px"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * In-page link down to the value calculator section.
 *
 * Plain anchor on purpose: the click handler upgrades it to a smooth scroll when the target
 * is actually on the page, and otherwise the native hash jump is left to do its job.
 *
 * @component
 * @param {Object} props
 * @param {string} props.targetSectionId id of the value calculator section
 * @returns {JSX.Element}
 */
const ValueCalculatorCTA = props => {
  const { targetSectionId } = props;

  const handleClick = e => {
    const target = document.getElementById(targetSectionId);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a className={css.calculatorCta} href={`#${targetSectionId}`} onClick={handleClick}>
      <FormattedMessage id="SectionEmotoHero.calculatorCta" />
    </a>
  );
};

/**
 * Landing page hero: eyebrow, display headline, value proposition, a link down to the
 * value calculator, category quick-links, and a shelf of the newest listings.
 *
 * Note: there is deliberately no search input here. The Topbar's search form is present on
 * every page, so a second one in the hero was redundant.
 *
 * All category data is read at runtime from the hosted `listing-categories`
 * asset via `useConfiguration()`, so nothing here is hardcoded to a marketplace.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className extra class names for the root element
 * @param {string?} props.rootClassName overwrite the component's own css.root
 * @param {string?} props.sectionId id used as the section anchor
 * @param {Object?} props.options section options from SectionBuilder (carries featuredListings)
 * @param {Array<Object>?} props.allSections all sections on the page
 * @param {string?} props.valueCalculatorSectionId anchor id of the value calculator section.
 *   When omitted, the calculator CTA is not rendered — the calculator isn't on the page.
 * @returns {JSX.Element} hero section element
 */
const SectionEmotoHero = props => {
  const {
    className,
    rootClassName,
    sectionId,
    options,
    allSections = [],
    valueCalculatorSectionId,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();

  // Guard: categoryConfiguration may be missing entirely if the hosted asset
  // hasn't been authored yet. Entries without an `id` are dropped — they would
  // link to `?pub_categoryLevel1=undefined` and collide on the React key.
  const categories = (config?.categoryConfiguration?.categories || []).filter(c => !!c?.id);
  const chipCategories = categories.slice(0, MAX_CHIPS);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes} id={sectionId}>
      <div className={css.content}>
        <p className={css.eyebrow}>
          <FormattedMessage id="SectionEmotoHero.eyebrow" />
        </p>

        <h1 className={css.heading}>
          <FormattedMessage id="SectionEmotoHero.titleLine1" />
          <br />
          <span className={css.headingAccent}>
            <FormattedMessage id="SectionEmotoHero.titleLine2" />
          </span>
        </h1>

        <p className={css.valueProp}>
          <FormattedMessage id="SectionEmotoHero.valueProp" />
        </p>

        <div className={css.heroActionRow}>
          <div className={css.heroActionBlock}>
            <NamedLink className={css.primaryCtaBtn} name="SearchPage">
              <FormattedMessage id="SectionEmotoHero.browseBtn" />
            </NamedLink>
            <p className={css.actionDef}>
              <FormattedMessage id="SectionEmotoHero.browseDef" />
            </p>
          </div>
          <div className={css.heroActionBlock}>
            <NamedLink className={css.primaryCtaBtn} name="NewListingPage">
              <FormattedMessage id="SectionEmotoHero.sellBtn" />
            </NamedLink>
            <p className={css.actionDef}>
              <FormattedMessage id="SectionEmotoHero.sellDef" />
            </p>
          </div>
        </div>

        {valueCalculatorSectionId ? (
          <ValueCalculatorCTA targetSectionId={valueCalculatorSectionId} />
        ) : null}

        {chipCategories.length > 0 ? (
          <nav
            className={css.chipRow}
            aria-label={intl.formatMessage({ id: 'SectionEmotoHero.chipsLabel' })}
          >
            {chipCategories.map(category => (
              // NamedLink resolves the SearchPage pathname from route config and keeps
              // cmd/ctrl/middle-click working. The previous hand-rolled <a> called
              // preventDefault() unconditionally, which broke "open in new tab".
              <NamedLink
                key={category.id}
                className={css.chip}
                name="SearchPage"
                to={{
                  search: `?${CATEGORY_LEVEL1_PARAM}=${encodeURIComponent(category.id)}`,
                }}
              >
                {category.name || category.id}
              </NamedLink>
            ))}
          </nav>
        ) : null}

        <HeroListings
          sectionId={sectionId}
          allSections={allSections}
          featuredListings={options?.featuredListings}
        />
      </div>
    </section>
  );
};

export default SectionEmotoHero;
